import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '../../api/client'
import { PageLayout } from '../../components/common/PageLayout'
import { Section, StatCard } from '../../components/common/DesignComponents'
import { Breadcrumb } from '../../components/common/NavigationComponents'
import { ProgressItem } from '../../components/common/LayoutPatterns'

interface SkillResponse {
  id: string
  name: string
  description: string
  icon: string
  stage: string
  requiredLevel: number
  statBonuses: Record<string, number>
  isUnlocked: boolean
  canUnlock: boolean
  progress: number
  isMastered: boolean
}

interface SkillTreeData {
  skills: SkillResponse[]
  currentLevel: number
  totalSkills: number
  unlockedCount: number
}

export const SkillTreePage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>()
  const navigate = useNavigate()
  
  const [skills, setSkills] = useState<SkillResponse[]>([])
  const [data, setData] = useState<SkillTreeData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unlocking, setUnlocking] = useState<string | null>(null)
  const [selectedSkill, setSelectedSkill] = useState<SkillResponse | null>(null)

  useEffect(() => {
    fetchSkills()
  }, [childId])

  const fetchSkills = async () => {
    try {
      setLoading(true)
      const response = await axiosClient.get(`/api/children/${childId}/skills`)
      if (response.data.success) {
        setData(response.data.data)
        setSkills(response.data.data.skills)
      }
    } catch (err) {
      setError('Failed to load skills')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleUnlockSkill = async (skillId: string) => {
    try {
      setUnlocking(skillId)
      const response = await axiosClient.post(`/api/children/${childId}/skills/unlock`, {
        skillId,
      })
      if (response.data.success) {
        await fetchSkills()
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to unlock skill')
    } finally {
      setUnlocking(null)
    }
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin text-5xl mb-4">🎯</div>
            <p className="text-purple-600 font-semibold">Loading skill tree...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout>
        <Section>
          <div className="text-center py-12">
            <p className="text-red-600 mb-4 text-lg">❌ {error}</p>
            <button
              onClick={() => navigate('/children')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
            >
              Back to Children
            </button>
          </div>
        </Section>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <Section>
        {/* Header */}
        <Breadcrumb
          items={[
            { label: 'Children', href: '/children' },
            { label: 'Skill Tree', current: true },
          ]}
          className="mb-6"
        />

        <div className="mb-8">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-6">
            🎯 Skill Tree
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              label="Current Level"
              value={data?.currentLevel.toString() || '0'}
              icon="📊"
              className="bg-gradient-to-br from-blue-50 to-indigo-50"
            />
            <StatCard
              label="Skills Unlocked"
              value={`${data?.unlockedCount || 0}/${data?.totalSkills || 0}`}
              icon="🔓"
              className="bg-gradient-to-br from-green-50 to-emerald-50"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Skills List */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-purple-600 mb-6">Available Skills</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {skills.map((skill) => (
                <div
                  key={skill.id}
                  onClick={() => setSelectedSkill(skill)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition ${
                    selectedSkill?.id === skill.id
                      ? 'border-purple-400 bg-purple-100'
                      : 'border-purple-200 hover:border-purple-400 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-3xl">{skill.icon}</span>
                    {skill.isMastered && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold">
                        ⭐ MASTERED
                      </span>
                    )}
                    {skill.isUnlocked && !skill.isMastered && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                        ✓ UNLOCKED
                      </span>
                    )}
                    {!skill.isUnlocked && skill.canUnlock && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                        ⚡ AVAILABLE
                      </span>
                    )}
                    {!skill.isUnlocked && !skill.canUnlock && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-bold">
                        🔒 LOCKED
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-purple-600">{skill.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {skill.isUnlocked
                      ? `Progress: ${skill.progress}%`
                      : `Requires Level ${skill.requiredLevel}`}
                  </p>

                  {/* Progress Bar */}
                  {skill.isUnlocked && (
                    <div className="mb-3">
                      <div className="w-full bg-purple-100 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all"
                          style={{ width: `${skill.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Unlock Button */}
                  {!skill.isUnlocked && skill.canUnlock && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleUnlockSkill(skill.id)
                      }}
                      disabled={unlocking === skill.id}
                      className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-400 transition"
                    >
                      {unlocking === skill.id ? '⏳ Unlocking...' : '⚡ Unlock Skill'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Skill Details */}
          <div className="lg:col-span-1">
            {selectedSkill ? (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 p-6 sticky top-6">
                <h3 className="text-2xl font-bold text-purple-600 mb-2 flex items-center gap-2">
                  <span className="text-4xl">{selectedSkill.icon}</span>
                  {selectedSkill.name}
                </h3>

                <div className="mb-6">
                  <p className="text-gray-600">{selectedSkill.description}</p>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-purple-600 mb-3">Stat Bonuses</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedSkill.statBonuses).map(([stat, bonus]) => (
                      <div key={stat} className="flex justify-between items-center p-2 bg-white rounded-lg">
                        <span className="text-sm text-gray-600 capitalize font-semibold">{stat}</span>
                        <span className="text-sm font-bold text-green-600">+{bonus}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-6 p-4 bg-white rounded-lg border border-purple-100">
                  <p className="text-sm text-gray-600 mb-2">Stage</p>
                  <p className="font-semibold text-purple-600 capitalize">{selectedSkill.stage}</p>
                </div>

                {selectedSkill.isUnlocked && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-2">Progress</p>
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      {selectedSkill.progress}%
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all"
                        style={{ width: `${selectedSkill.progress}%` }}
                      ></div>
                    </div>
                    {selectedSkill.isMastered && (
                      <p className="text-center text-yellow-600 font-bold mt-2">✅ MASTERED!</p>
                    )}
                  </div>
                )}

                {!selectedSkill.isUnlocked && (
                  <div className="p-4 bg-blue-50 rounded-lg text-center border border-blue-200">
                    <p className="text-blue-700 font-semibold">
                      🔓 Unlock at Level {selectedSkill.requiredLevel}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200 p-6 text-center">
                <p className="text-gray-600">👆 Select a skill to view details</p>
              </div>
            )}
          </div>
        </div>
      </Section>
    </PageLayout>
  )
}

export default SkillTreePage
